import { createRouter, createWebHistory, RouterView } from 'vue-router';
import i18n from '@/i18n';

import LandingPage from '@/components/screens/LandingPage.vue';
import SectorPage from '@/components/screens/SectorPage.vue';
import ContactUs from '@/components/screens/ContactUs.vue';
import DisplayerPage from '@/components/screens/DisplayerPage';
import SectorList from '@/components/screens/SectorList.vue';
import SearchResults from '@/components/screens/SearchResults.vue';
import PrivateList from '@/components/screens/PrivateList.vue';
import CardPage from '@/components/screens/CardPage.vue';
import PublicPersonalities from '@/components/screens/PublicPersonalities.vue';
import PublicPersonalityDetail from '@/components/screens/PublicPersonalityDetail.vue';
import EmbassyDetail from '@/components/screens/EmbassyDetail.vue';
import ChatBot from '@/components/chatbot/ChatBot.vue';

// Registration portal
import RegisterPage      from '@/components/screens/registration/RegisterPage.vue';
import LoginPage         from '@/components/screens/registration/LoginPage.vue';
import VerifyEmailPage   from '@/components/screens/registration/VerifyEmailPage.vue';
import DashboardPage     from '@/components/screens/registration/DashboardPage.vue';
import AdminPortal       from '@/components/screens/registration/AdminPortal.vue';
import AdminPendingPage  from '@/components/screens/registration/AdminPendingPage.vue';

const appRoutes = [
  { path: '', name: 'home', component: LandingPage },
  { path: 'home', redirect: { name: 'home' } },
  { path: 'sector-page', name: 'sector-page', component: SectorPage },
  { path: 'contact', name: 'contact', component: ContactUs },
  {
    path: 'structure-details/:name',
    name: 'structure-details',
    component: DisplayerPage,
    props: true
  },
  {
    path: 'structure-list/:name',
    name: 'structure-list',
    component: SectorList,
    props: true,
    beforeEnter: async (to, from, next) => {
      const { useSectorStore } = await import('@/stores/sector');
      const sectorStore = useSectorStore();
      sectorStore._ensureCacheLoaded();
      if (!sectorStore.currentSectorId) {
        next(from.fullPath || '/');
      } else {
        next();
      }
    }
  },
  {
    path: 'private-structure-list/:name',
    name: 'private-structure-list',
    component: PrivateList,
    props: true,
    beforeEnter: async (to, from, next) => {
      const { useSectorStore } = await import('@/stores/sector');
      const sectorStore = useSectorStore();
      sectorStore._ensureCacheLoaded();
      if (!sectorStore.currentSectorId) {
        next(from.fullPath || '/');
      } else {
        next();
      }
    }
  },
  {
    path: 'public-personalities',
    name: 'public-personalities',
    component: PublicPersonalities
  },
  {
    path: 'public-personalities/:name',
    name: 'public-personality-detail',
    component: PublicPersonalityDetail,
    props: true
  },
  {
    path: 'embassies/:name',
    name: 'embassy-detail',
    component: EmbassyDetail,
    props: true
  },
  {
    path: 'search-results',
    name: 'search-results',
    component: SearchResults,
    props: (route) => ({
      query: route.query.q,
      type: route.query.type,
      id: route.query.id
    })
  },
  {
    path: 'categories/:sectorType',
    name: "categories",
    component: CardPage,
  },
  { path: 'private-list/:id', name: "private-list-by-id", component: PrivateList },
  { path: 'sector-list/:id', name: 'sector-list-by-id', component: SectorList },
  { path: 'sector', name: 'sector', component: SectorList },
  {
    path: 'chatbot',
    name: 'chatbot',
    component: ChatBot,
  },

  // ── Registration Portal ───────────────────────────────────────────────────
  { path: 'register',                                       name: 'register',         component: RegisterPage },
  { path: 'register/login',                                 name: 'reg-login',        component: LoginPage },
  { path: 'register/verify-email',                          name: 'reg-verify',       component: VerifyEmailPage },
  { path: 'register/verify-email/:user_id/:token',          name: 'reg-verify-token', component: VerifyEmailPage },
  { path: 'register/dashboard',                             name: 'reg-dashboard',    component: DashboardPage },
  { path: 'register/admin',                                 name: 'admin-portal',     component: AdminPortal },
  { path: 'register/admin/pending',                         name: 'admin-pending',    component: AdminPendingPage },
];


const routes = [
  {
    path: '/',
    redirect: () => {
      const lang = localStorage.getItem('user-language') || navigator.language.split('-')[0] || 'en';
      const supportedLang = lang === 'fr' ? 'fr' : 'en';
      return `/${supportedLang}`;
    },
  },
  {
    path: '/:lang',
    component: RouterView,
    children: appRoutes,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return new Promise((resolve) => {
        const maxTries = 20;
        let currentTry = 0;
        const interval = setInterval(() => {
          currentTry++;
          if (document.querySelector('.embassies-container') || currentTry >= maxTries) {
            clearInterval(interval);
            resolve(savedPosition);
          }
        }, 100);
      });
    } else {
      return { top: 0, behavior: 'smooth' };
    }
  },
});

router.beforeEach((to, from, next) => {
  const lang = to.params.lang;

  if (!['en', 'fr'].includes(lang)) {
    const defaultLang = localStorage.getItem('user-language') || 'en';
    const intendedPath = to.fullPath.startsWith('/') ? to.fullPath.substring(1) : to.fullPath;
    return next(`/${defaultLang}/${intendedPath}`);
  }

  // With legacy: false, i18n.global.locale is a Vue Ref — must use .value
  const currentLocale = typeof i18n.global.locale === 'object' ? i18n.global.locale.value : i18n.global.locale;
  if (currentLocale !== lang) {
    if (typeof i18n.global.locale === 'object') {
      i18n.global.locale.value = lang;
    } else {
      i18n.global.locale = lang;
    }
    localStorage.setItem('user-language', lang);
  }

  return next();
});

export default router;